import * as d3 from 'd3';
import * as moment from 'moment';
import * as $ from 'jquery';
import { ResultsStatus } from '../../models/enum';
import getElementSize from '../../common/helpers/getElementSize';

import { resultsStatusScale, symbolsScale } from '../../common/scales';
import { getAxisFormat } from '../../wtFramework';

const timeFormat = d3.time.format('%Y-%m-%d %H:%M');

export interface Margins {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface DateProcessingResult {
    timeStamp: Date;
    status: ResultsStatus;
    message: string | null;
    parameters?: { [key: string]: any };
}

export interface ResultsFormat {
    stepName: string;
    results: DateProcessingResult[];
}

export class GroupStepDetailsChart {
    _seriesgroup: d3.Selection<any>;
    _xAxis: d3.svg.Axis;
    _xScale: d3.time.Scale<number, number>;
    _xAxisGroup: d3.Selection<any>;
    _yAxisGroup: d3.Selection<any>;
    _yAxis: d3.svg.Axis;
    _yScale: d3.scale.Ordinal<string, number>;
    private _svg: d3.Selection<any>;
    private _width: number = 400;
    private _height: number = 300;

    private _plotMargins: Margins = {
        top: 20,
        bottom: 5,
        left: 150,
        right: 5
    };
    private _messageFormat: (d: DateProcessingResult) => string;

    constructor(selector: string) {
        this._svg = d3.select(selector)
            .select('svg')
            .attr('width', this._width)
            .attr('height', this._height);

        const plotGroup = this._svg.append('g')
            .classed('plot', true)
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top})`);

        this._xAxisGroup = plotGroup.append('g')
            .classed('axis', true);
        this._yAxisGroup = plotGroup.append('g')
            .classed('axis', true);
        this._seriesgroup = plotGroup.append('g')
            .classed('series', true);

        const plotWidth = this._width - this._plotMargins.left - this._plotMargins.right;
        const plotHeight = this._height - this._plotMargins.top - this._plotMargins.bottom;

        this._yScale = d3.scale.ordinal()
            .rangeBands([0, plotHeight]);
        this._yAxis = d3.svg.axis()
            .orient('left')
            .scale(this._yScale);
        this._xScale = d3.time.scale()
            .range([0, plotWidth]);
        this._xAxis = d3.svg.axis()
            .orient('top')
            .scale(this._xScale);

        $(selector).resize(() => {
            const size = getElementSize(selector);
            this.resize(size);
        });

        this._messageFormat = d => {
            let fmt = `${timeFormat(d.timeStamp)}`;
            if (d.message && d.message !== 'null') {
                fmt = `${fmt} : ${d.message}`;
            }
            return fmt;
        };
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (arguments.length) {
            if (value !== this._width) {
                this._width = value;
                this._svg.attr('width', this._width);
                const plotWidth = this._width - this._plotMargins.left - this._plotMargins.right;
                this._xScale.range([0, plotWidth]);
            }
            return this;
        }
        return this._width;
    }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (arguments.length) {
            if (value !== this._height) {
                this._height = value;
                this._svg.attr('height', this._height);
                const plotHeight = this._height - this._plotMargins.top - this._plotMargins.bottom;
                this._yScale.rangeBands([0, plotHeight]);
            }
            return this;
        }
        return this._height;
    }

    yPos(): 'left' | 'right';
    yPos(value: 'left' | 'right'): this;
    yPos(value?: 'left' | 'right'): this | 'left' | 'right' {
        const orient = this._yAxis.orient();
        if (arguments.length) {
            if (value !== orient) {
                this._yAxis.orient(value);
            }
            return this;
        }
        return orient as 'left' | 'right';
    }

    labelFormat(value: (d: any) => string): this {
        this._yAxis.tickFormat(d => value(d));
        return this;
    }

    messageFormat(): (d: DateProcessingResult) => string;
    messageFormat(value: (d: DateProcessingResult) => string): this;
    messageFormat(value?: (d: DateProcessingResult) => string): this | ((d: DateProcessingResult) => string) {
        if (arguments.length) {
            this._messageFormat = value;
            return this;
        }
        return this._messageFormat;
    }

    margins(): Margins;
    margins(value: Margins): this;
    margins(value?: Margins): this | Margins {
        if (arguments.length) {
            this._plotMargins = value;
            return this;
        }
        return this._plotMargins;
    }


    render(data: ResultsFormat[]) {
        const dates = [].concat(...data.map(d => d.results))
            .map(d => d.timeStamp);
        const extent = d3.extent(dates);
        const duration = moment.duration(moment(extent[1]).diff(moment(extent[0])));
        const { format, ticks } = getAxisFormat(duration, this.width(), extent, true);
        this._xAxis.tickValues(ticks)
            .tickFormat(d3.time.format(format.format));
        this._xScale
            .domain(extent);
        this._yScale
            .domain(data.map(d => d.stepName));
        const dataBound = this.renderSteps(data);
        this.layout(dataBound);
    }

    private resize(size: { width: number, height: number }): void {
        this.width(size.width)
            .height(size.height)
            .layout(d3.selectAll('.step'));
    }

    private renderSteps(data: ResultsFormat[]) {
        const dataBound = this._seriesgroup.selectAll('.step')
            .data(data);
        dataBound
            .exit()
            .remove();
        const enterSelection = dataBound
            .enter()
            .append('g')
            .classed('step', true);
        this.renderSymbols(dataBound);
        return dataBound;
    }

    private renderSymbols(dataBound: d3.selection.Update<ResultsFormat>) {
        const symbolsBound = dataBound.selectAll('.symbol')
            .data(d => d.results);
        symbolsBound
            .exit()
            .remove();
        const symbolsEnter = symbolsBound
            .enter()
            .append('g')
            .classed('symbol', true)
            .style('pointer-events', 'all');
        symbolsEnter.append('path');
        symbolsEnter.append('title');
    }

    private layout(stepBound: d3.Selection<any>) {
        this._svg.select('g.plot')
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top})`);
        this._xAxisGroup
            .transition()
            .call(this._xAxis);
        const plotWidth = this._width - this._plotMargins.left - this._plotMargins.right;

        this._yAxisGroup
            .attr('transform', `translate(${this._yAxis.orient() === 'left' ? 0 : plotWidth},0)`)
            .call(this._yAxis);

        this._xAxisGroup.selectAll('.tick')
            .select('text')
            .classed('tickLabel', true);
        this._yAxisGroup.selectAll('.tick')
            .select('text')
            .classed('tickLabel', true);

        stepBound
            .attr('transform', (d, i) => `translate(${0},${this._yScale(d.stepName)})`);
        const symbolsBound: d3.Selection<DateProcessingResult> = stepBound.selectAll('.symbol');
        symbolsBound.attr('transform', (d, i) => `translate(${this._xScale(d.timeStamp)},${this._yScale.rangeBand() / 2})`)
            .select('path')
            .attr('d', (d, i) => d3.svg.symbol()
                .size(30)
                .type(symbolsScale(d.status))(d, i))
            .attr('transform', d => {
                return `rotate(${d.status === ResultsStatus.Error ? 45 : 0})`;
            })
            .style('fill', d => d.message === 'No data' ? 'transparent' : resultsStatusScale(d.status))
            ;
        symbolsBound.select('title')
            .text(this._messageFormat);
    }
}